// @ts-strict-ignore
import {
  CONTENTFUL_CONTENT_TYPES,
  CONTENTFUL_REQUEST_LIMIT,
  CONTENTFUL_REQUEST_LIMIT_LARGE,
  CONTENTFUL_REQUEST_LIMIT_MAX
} from "constants/api-constants";
import { contentfulClientCryptopedia as client } from "../contentful";
import { validateLocale } from "utils/validateLocale";

const CryptopediaAPI: any = {};

CryptopediaAPI.fetchArticlesByTopicId = (topicId, locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_ARTICLE,
    limit: CONTENTFUL_REQUEST_LIMIT_MAX,
    locale: validateLocale(locale),
    order: "sys.createdAt",
    "fields.topics.sys.id": topicId
  });

CryptopediaAPI.fetchArticles = (locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_ARTICLE,
    limit: CONTENTFUL_REQUEST_LIMIT_LARGE,
    locale: validateLocale(locale),
    order: "sys.createdAt"
  });

CryptopediaAPI.fetchArticlesByAuthorId = (id, locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_ARTICLE,
    limit: CONTENTFUL_REQUEST_LIMIT_LARGE,
    order: "sys.createdAt",
    locale: validateLocale(locale),
    "fields.author.sys.id": id
  });

CryptopediaAPI.fetchArticlesCount = async () => {
  const result = await client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_ARTICLE,
    limit: CONTENTFUL_REQUEST_LIMIT_LARGE,
    order: "sys.createdAt",
    select: "fields.title"
  });
  return result.total;
};

CryptopediaAPI.fetchArticleWithSlug = (slug: string, locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_ARTICLE,
    locale: validateLocale(locale),
    "fields.slug": slug
  });

CryptopediaAPI.fetchCategories = (locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_CATEGORY,
    locale: validateLocale(locale),
    order: "fields.order"
  });

CryptopediaAPI.fetchCategoriesCount = async () => {
  const result = await client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_CATEGORY,
    order: "fields.order",
    select: "fields.name"
  });
  return result.total;
};

CryptopediaAPI.fetchCategoryById = (id: string, locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_CATEGORY,
    locale: validateLocale(locale),
    "sys.id": id
  });

CryptopediaAPI.fetchTopics = (locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_TOPIC,
    "fields.slug[exists]": true,
    limit: CONTENTFUL_REQUEST_LIMIT_MAX,
    locale: validateLocale(locale),
    order: "fields.name"
  });

CryptopediaAPI.fetchFewTopics = (locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_TOPIC,
    "fields.slug[exists]": true,
    limit: CONTENTFUL_REQUEST_LIMIT,
    locale: validateLocale(locale),
    order: "fields.name"
  });

CryptopediaAPI.fetchTopicsCount = async () => {
  const result = await client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_TOPIC,
    "fields.slug[exists]": true,
    limit: CONTENTFUL_REQUEST_LIMIT_MAX,
    order: "fields.name"
  });
  return result.total;
};

CryptopediaAPI.fetchTopicWithSlug = (slug: string, locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_TOPIC,
    locale: validateLocale(locale),
    "fields.slug": slug
  });

CryptopediaAPI.fetchFewStories = (locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_STORY,
    "fields.slug[exists]": true,
    locale: validateLocale(locale),
    limit: CONTENTFUL_REQUEST_LIMIT
  });

CryptopediaAPI.fetchHomepageStories = (locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_STORY,
    "fields.slug[exists]": true,
    order: "fields.order",
    locale: validateLocale(locale),
    limit: 5
  });

CryptopediaAPI.fetchStoryWithSlug = (slug: string, locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_STORY,
    locale: validateLocale(locale),
    "fields.slug": slug
  });

CryptopediaAPI.fetchAuthors = (locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_AUTHOR,
    limit: CONTENTFUL_REQUEST_LIMIT_LARGE,
    locale: validateLocale(locale),
    order: "fields.name",
    "fields.slug[exists]": true
  });

CryptopediaAPI.fetchAuthorsCount = async () => {
  const results = await client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_AUTHOR,
    limit: CONTENTFUL_REQUEST_LIMIT_LARGE,
    order: "fields.name",
    "fields.slug[exists]": true,
    select: "fields.name"
  });
  return results.total;
};

CryptopediaAPI.fetchAuthorWithSlug = (slug: string, locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_AUTHOR,
    locale: validateLocale(locale),
    "fields.slug": slug
  });

CryptopediaAPI.fetchGlossaryEntries = (skip?: number, locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_GLOSSARY,
    order: "fields.name",
    "fields.name[exists]": true,
    "fields.definition[exists]": true,
    locale: validateLocale(locale),
    ...(skip && { skip }),
    limit: CONTENTFUL_REQUEST_LIMIT_MAX
  });

CryptopediaAPI.fetchArticlesByCoin = (
  coinId: string,
  limit?: number,
  locale?: string
) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_ARTICLE,
    locale: validateLocale(locale),
    include: 5,
    limit: limit ?? 2,
    "fields.relatedCoinsSlug.sys.id": coinId
  });

CryptopediaAPI.fetchCoinsBySlug = (slug: string, locale?: string) =>
  client.getEntries({
    content_type: CONTENTFUL_CONTENT_TYPES.CRYPTOPEDIA_COIN_PAGE_SLUG,
    locale: validateLocale(locale),
    include: 5,
    "fields.slug": slug
  });

export default CryptopediaAPI;
